/**
 * ProfileService.ts
 *
 * Service pour gérer les profils utilisateur
 * PR-05 : Auth robuste + profil garanti
 */

import { getSupabaseClient } from './SupabaseService';
import { logger } from '../utils/ProfessionalLogger';

/**
 * Vérifie et crée un profil si absent
 *
 * @param userId - ID de l'utilisateur
 * @returns Promise<void>
 *
 * @example
 * ```typescript
 * await ProfileService.ensureProfile(user.id);
 * ```
 */
export async function ensureProfile(userId: string): Promise<void> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    logger.warn('[ProfileService] Supabase non configuré, impossible de créer le profil');
    return;
  }

  try {
    // Vérifier si le profil existe
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      // PGRST116 = "no rows returned", ce qui est OK (profil n'existe pas)
      throw checkError;
    }

    // Si le profil existe déjà, on a terminé
    if (existingProfile) {
      logger.debug(`[ProfileService] Profil ${userId} existe déjà`);
      return;
    }

    // Créer le profil
    const { error: createError } = await supabase.from('profiles').insert({
      id: userId,
      is_admin: false,
      preferences: {},
    });

    if (createError) {
      // Si erreur de contrainte unique, le profil a été créé entre temps (race condition)
      if (createError.code === '23505') {
        logger.debug(`[ProfileService] Profil ${userId} créé entre temps (race condition)`);
        return;
      }
      throw createError;
    }

    logger.info(`[ProfileService] Profil ${userId} créé avec succès`);
  } catch (error) {
    logger.error(`[ProfileService] Erreur lors de la création du profil ${userId}:`, error);
    // Ne pas throw pour éviter de bloquer l'authentification
    // Le trigger SQL créera le profil de toute façon
  }
}

/**
 * Met à jour le profil utilisateur
 */
export async function updateProfile(
  userId: string,
  updates: {
    full_name?: string;
    avatar_url?: string;
    preferences?: Record<string, unknown>;
  }
): Promise<{ error: Error | null }> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return { error: new Error('Supabase non configuré') };
  }

  try {
    // P3 : `preferences` typé Record<string, unknown> côté input mais Json côté Supabase.

    const { error } = await supabase
      .from('profiles')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(updates as any)
      .eq('id', userId);

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: error as Error };
  }
}

/**
 * Récupère le profil utilisateur
 */
export async function getProfile(userId: string) {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return { data: null, error: new Error('Supabase non configuré') };
  }

  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();

  return { data, error };
}
